import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, useRouteModulePermissions } from "@/components/common/Crud";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/common/DatePicker";

export default function Reports() {
  const permissions = useRouteModulePermissions("reports");
  const { branchId, sessionId } = useAuth();
  const [collection, setCollection] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [absent, setAbsent] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    const params = { branch_id: branchId, session_id: sessionId };
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const [c, o, a] = await Promise.all([
      api.get("/fees/collection-summary", { params }),
      api.get("/fees/outstanding", { params }),
      api.get("/attendance/absent-today", { params: { branch_id: branchId } }),
    ]);
    setCollection(c.data);
    setOutstanding(o.data);
    setAbsent(a.data);
  };

  useEffect(() => {
    load();
  }, [branchId, sessionId]);

  if (!permissions.canView) {
    return (
      <div
        data-testid="reports-page"
        className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500"
      >
        No reports access assigned for this role.
      </div>
    );
  }

  return (
    <div data-testid="reports-page">
      <PageTitle
        title="Reports"
        subtitle="Fees collection, outstanding, attendance & more"
      />
      <div className="flex gap-2 mb-3 items-end">
        <div>
          <Label className="text-xs font-semibold block mb-1">From</Label>
          <DatePicker
            value={from}
            onChange={setFrom}
            className="h-8 w-40 text-xs"
            testId="rep-from"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold block mb-1">To</Label>
          <DatePicker
            value={to}
            onChange={setTo}
            className="h-8 w-40 text-xs"
            testId="rep-to"
          />
        </div>
        <Button
          onClick={load}
          className="h-8 bg-slate-900 hover:bg-slate-800"
          data-testid="rep-apply"
        >
          Apply
        </Button>
      </div>
      <Tabs defaultValue="collection">
        <TabsList>
          <TabsTrigger value="collection" data-testid="rep-tab-collection">
            Collection
          </TabsTrigger>
          <TabsTrigger value="outstanding" data-testid="rep-tab-outstanding">
            Outstanding
          </TabsTrigger>
          <TabsTrigger value="absent" data-testid="rep-tab-absent">
            Absent Today
          </TabsTrigger>
        </TabsList>
        <TabsContent value="collection" className="mt-3">
          {collection && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="border border-slate-200 bg-white rounded-md p-4">
                <div className="text-xs uppercase text-slate-500 font-semibold mb-2">
                  Total Collected
                </div>
                <div className="text-3xl font-bold tracking-tight">
                  ₹{Number(collection.total).toLocaleString()}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {collection.count} receipts
                </div>
              </div>
              <div className="border border-slate-200 bg-white rounded-md p-4">
                <div className="text-xs uppercase text-slate-500 font-semibold mb-2">
                  By Mode
                </div>
                {Object.entries(collection.by_mode || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1 text-sm">
                    <span>{k}</span>
                    <span className="font-mono">₹{v.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="border border-slate-200 bg-white rounded-md p-4">
                <div className="text-xs uppercase text-slate-500 font-semibold mb-2">
                  By Fee Head
                </div>
                {Object.entries(collection.by_head || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1 text-sm">
                    <span>{k}</span>
                    <span className="font-mono">₹{v.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="outstanding" className="mt-3">
          {outstanding && (
            <div className="border border-slate-200 bg-white rounded-md p-4">
              <div className="text-xs uppercase text-slate-500 font-semibold mb-2">
                Total Outstanding
              </div>
              <div className="text-3xl font-bold tracking-tight text-red-600">
                ₹{Number(outstanding.total_due).toLocaleString()}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {outstanding.rows?.length || 0} students with dues
              </div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="absent" className="mt-3">
          {absent && (
            <div className="border border-slate-200 bg-white rounded-md p-4">
              <div className="text-xs uppercase text-slate-500 font-semibold mb-2">
                Absent Today
              </div>
              <div className="text-3xl font-bold tracking-tight text-amber-600">
                {absent.count}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Students absent today
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
